import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './components/Auth/LoginPage';
import PrivateRoute from './components/Auth/PrivateRoute';
import AppSelectionPage from './components/AppSelection/AppSelectionPage';
import AppDetailPage from './pages/AppDetailPage'; // Main page for managing a selected app
import DashboardPage from './pages/DashboardPage';
import ArticlesPage from './pages/ArticlesPage';
import ChatGroupsPage from './pages/ChatGroupsPage';
import EventsPage from './pages/EventsPage';
import MapPoisPage from './pages/MapPoisPage'; // The POI page we added
import UsersPage from './pages/UsersPage'; // Import the new UsersPage
import KnowledgeBasePage from './pages/KnowledgeBasePage'; // Import Knowledge Base Page
import ChatPage from './pages/ChatPage'; // Import Chat Page
import AiPipelinePage from './pages/AiPipelinePage'; // new page
import VouchersPage from './pages/VouchersPage'; // Import Vouchers Page
import PublicationRankingPage from './pages/PublicationRankingPage';
import AppSettingsPage from './pages/AppSettingsPage';
import OrganizationsPage from './pages/OrganizationsPage';
import OrgMembersPage from './pages/OrgMembersPage';
import AddAdminUserPage from './pages/AddAdminUserPage';
import EditAdminUserPage from './pages/EditAdminUserPage';
// Removed imports for non-existent pages: ManageAdminsPage, ManageAppsPage, NotFoundPage, ProfilePage, StatsPage

import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// Import react-query client and provider
import { QueryClient, QueryClientProvider } from 'react-query';

// Create a client instance
const queryClient = new QueryClient();

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );
  }

  return (
    <Routes>
      {/* Public Route: Login */}
      <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/select-app" replace />} />

      {/* Private Routes: Require authentication */}
      <Route element={<PrivateRoute />}>

        {/* App Selection Route */}
        <Route path="/select-app" element={<AppSelectionPage />} />


        {/* App Detail Route - This uses Outlet for nested content */}
        <Route path="/app/:appId" element={<AppDetailPage />}>
            {/* Default child route (index) -> Redirect to Dashboard */}
            <Route index element={<Navigate to="dashboard" replace />} />
            {/* Nested routes rendered by AppDetailPage's Outlet */}
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="articles" element={<ArticlesPage />} />
            <Route path="chat-groups" element={<ChatGroupsPage />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="map-pois" element={<MapPoisPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="ai-pipeline" element={<AiPipelinePage />} />
            <Route path="publication-ranking" element={<PublicationRankingPage />} />
            <Route path="vouchers" element={<VouchersPage />} />
             <Route path="settings" element={<AppSettingsPage />} />
            <Route path="org-members" element={<OrgMembersPage />} />
            {/* Add future app-specific routes here, e.g., stats, users */}
            {/* <Route path="stats" element={<StatsPage />} /> */} 
        </Route>

        {/* Redirect base path "/" to app selection if logged in, else to login */}
        <Route path="/" element={<Navigate to={isAuthenticated ? "/select-app" : "/login"} replace />} />

        {/* Admin management routes (full_admin only, pages handle own auth/redirect) */}
        <Route path="/admins/new" element={<AddAdminUserPage />} />
        <Route path="/admins/:adminId" element={<EditAdminUserPage />} />

        {/* Add a simple 404 handler for any other authenticated routes */}
        {/* If Manage Admins/Apps pages are created later, they can be added here outside /app/:appId */}
        {/* <Route path="/manage-admins" element={<ManageAdminsPage />} /> */}
        {/* <Route path="/manage-apps" element={<ManageAppsPage />} /> */}
        {/* <Route path="/profile" element={<ProfilePage />} /> */}
        <Route path="*" element={<div>404 - Authenticated Page Not Found</div>} />

      </Route>

       {/* Fallback for any other unmatched routes (when not authenticated and not /login) */}
       <Route path="*" element={<div>404 - Page Not Found</div>} />
    </Routes>
  );
}

function App() {
  return (
    // Wrap the entire app with QueryClientProvider
    <QueryClientProvider client={queryClient}>
      <Router>
        <CssBaseline />
        <AuthProvider>
           {/* Wrap AppContent in Box to ensure layout consistency if needed */}
           <Box sx={{ display: 'flex', minHeight: '100vh' }}> 
               <AppContent />
           </Box>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
